"use client";

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { ErrorState } from "@/components/ui/ErrorState";
import { PageContainer } from "@/components/layout/PageContainer";

export default function LocaleError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // ChunkLoadError is already handled by instrumentation-client.ts (one-shot
    // reload). Capturing here too produces duplicate Sentry events for every
    // post-deploy stale-tab visitor.
    const isChunk =
      error?.name === "ChunkLoadError" ||
      /ChunkLoadError|Loading chunk|Failed to (load|fetch) (module|chunk)/i.test(
        error?.message || ""
      );
    if (isChunk) return;
    console.error("[error.tsx]", error);
    void import("@sentry/nextjs").then((Sentry) => {
      Sentry.captureException(error, {
        tags: { boundary: "locale-error" },
        contexts: {
          nextjs: { digest: error.digest },
          page: typeof window !== "undefined"
            ? { url: window.location.href, pathname: window.location.pathname }
            : undefined,
        },
      });
    }).catch(() => {});
  }, [error]);

  let t: (key: string) => string;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const translations = useTranslations("common");
    t = (key: string) => {
      try {
        return translations(key as any);
      } catch {
        return key;
      }
    };
  } catch {
    t = (key: string) => {
      const fallbacks: Record<string, string> = {
        "error.title": "Something went wrong",
        "error.description":
          "We ran into an unexpected problem. You can try again, or go back to the homepage.",
        "error.retry": "Try again",
        "error.goHome": "Go home",
      };
      return fallbacks[key] || key;
    };
  }

  return (
    <PageContainer width="default" paddingY="lg">
      <ErrorState
        title={t("error.title")}
        description={t("error.description")}
        onRetry={reset}
        homeHref="/"
        labels={{
          retry: t("error.retry"),
          home: t("error.goHome"),
        }}
      />
    </PageContainer>
  );
}
